import React from "react";
import { useNavigate } from "react-router-dom";
import PortfolioNavbar from "./PortfolioNavbar";
import PortfolioFooter from "./PortfolioFooter";
import PageHero from "./PageHero";
import { Helmet } from "react-helmet-async";
import {
  Users,
  Briefcase,
  Zap,
  Star,
  Globe,
  TrendingUp,
  MessageCircle,
  Award,
  Lightbulb,
  Cpu,
  Shield,
  Target,
  TrendingUp as ResultsIcon,
  Code as TechIcon,
  Layers as InterdisciplinaryIcon,
  Activity as LearningIcon,
  Monitor,
  Code,
  Cloud,
  Smartphone,
  CheckCircle,
  ArrowRight,
} from "lucide-react";

const OurWork = () => {
  const navigate = useNavigate();

  // Helper component for the hexagon icon (reused style)
  const HexagonIcon = ({ children, color }) => (
    <div
      className={`w-14 h-14 flex items-center justify-center p-1 rounded-lg shadow-lg`}
      style={{
        clipPath:
          "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
        background: color,
        transform: "rotate(0deg)",
      }}
    >
      {children}
    </div>
  );

  // --- Enhanced Content Data ---

  const mindsetPoints = [
    {
      icon: <TechIcon className="w-5 h-5 text-orange-600" />,
      title: "Engineering Excellence",
      detail:
        "Team of <span class='font-bold text-orange-600'>expert engineers</span> with strong full-stack & cloud backgrounds.",
    },
    {
      icon: <ResultsIcon className="w-5 h-5 text-orange-600" />,
      title: "Performance-First",
      detail:
        "Driven by a <span class='font-bold text-orange-600'>performance-first mentality</span>, committed to achieving <span class='font-bold text-orange-600'>99.9% system uptime</span>.",
    },
    {
      icon: <LearningIcon className="w-5 h-5 text-orange-600" />,
      title: "Continuous Innovation",
      detail:
        "Cultivating a culture of <span class='font-bold text-orange-600'>continuous R&D</span> and agile technical problem-solving.",
    },
    {
      icon: <InterdisciplinaryIcon className="w-5 h-5 text-orange-600" />,
      title: "Full-Stack Synergy",
      detail:
        "Our strength lies in <span class='font-bold text-orange-600'>interdisciplinary skills</span>, combining frontend finesse with robust backend architecture.",
    },
  ];

  const workSummary = [
    {
      label: "Foundational Success",
      detail:
        "Developed and deployed <span class='font-bold text-orange-600'>Enterprise ERP Systems</span> for leading manufacturing firms.",
    },
    {
      label: "Current Footprint",
      detail:
        "Currently architecting high-impact <span class='font-bold text-orange-600'>Fintech & Healthcare platforms</span> across global markets.",
    },
    {
      label: "Technical Focus",
      detail:
        "Expertise in <span class='font-bold text-orange-600'>distributed systems</span>, <span class='font-bold text-orange-600'>real-time analytics</span>, and <span class='font-bold text-orange-600'>security hardening</span>.",
    },
    {
      label: "Modern Backbone",
      detail:
        "All projects are built on modern stacks using <span class='font-bold text-orange-600'>React, Node.js, and AWS</span> for maximum scalability.",
    },
  ];

  const coreValues = [
    {
      icon: <Shield className="w-6 h-6 text-white" />,
      title: "Reliability & Security",
      description:
        "Maintaining absolute system stability and top-tier security standards in all software deployments.",
    },
    {
      icon: <Star className="w-6 h-6 text-white" />,
      title: "Technical Innovation",
      description:
        "Constantly adapting our tech stack and methodologies to stay ahead of industry standards.",
    },
    {
      icon: <Briefcase className="w-6 h-6 text-white" />,
      title: "Code Integrity",
      description:
        "Taking full ownership of code quality and providing measurable technical ROI for every client.",
    },
  ];

  const strategicFocus = [
    {
      icon: <Code className="w-5 h-5 text-orange-600" />,
      name: "Custom Software Dev",
      description:
        "High-performance, bespoke software solutions tailored to unique business requirements.",
    },
    {
      icon: <Smartphone className="w-5 h-5 text-orange-600" />,
      name: "Mobile Ecosystems",
      description:
        "Cross-platform and native mobile applications with focus on user engagement and performance.",
    },
    {
      icon: <Cloud className="w-5 h-5 text-orange-600" />,
      name: "Cloud Architecture",
      description:
        "Designing and managing scalable cloud-native infrastructures for global access.",
    },
    {
      icon: <Monitor className="w-5 h-5 text-orange-600" />,
      name: "Real-Time Observability",
      description:
        "Leveraging advanced monitoring tools for proactive system health and error management.",
    },
  ];

  const teamPhilosophy = [
    {
      title: "Collaborative Engineering",
      description:
        "Our cross-functional teams work in tandem, integrating insights from architecture, UI/UX, and DevOps to create cohesive products.",
      icon: <Award className="w-5 h-5 text-orange-600" />,
    },
    {
      title: "Data-Driven Development",
      description:
        "Every technical choice is informed by performance metrics and user data, ensuring maximum efficiency and impact.",
      icon: <Cpu className="w-5 h-5 text-orange-600" />,
    },
    {
      title: "Agile Adaptation",
      description:
        "We operate with flexibility, rapidly adjusting to changing requirements and emerging technologies to maintain momentum.",
      icon: <Zap className="w-5 h-5 text-orange-600" />,
    },
  ];

  const impactMetrics = [
    {
      metric: "Global Scalability",
      description:
        "Proven capability to architect systems supporting millions of concurrent users.",
      icon: <Globe className="w-5 h-5 text-orange-600" />,
    },
    {
      metric: "Rapid Deployment",
      description:
        "Consistent record of setting up production-ready CI/CD pipelines in under 2 hours.",
      icon: <TrendingUp className="w-5 h-5 text-orange-600" />,
    },
    {
      metric: "High Transaction Volume",
      description:
        "Delivering systems that process millions of requests daily with sub-second latency.",
      icon: <MessageCircle className="w-5 h-5 text-orange-600" />,
    },
    {
      metric: "Security Hardening",
      description:
        "Experienced in implementing zero-trust architectures and mitigating complex cyber threats.",
      icon: <Shield className="w-5 h-5 text-orange-600" />,
    },
  ];

  return (
    <div className="relative min-h-screen bg-gray-50 overflow-hidden font-sans">
      <Helmet>
        <title>Case Studies | Winsil Evolution Pvt Ltd</title>
        <meta name="description" content="Explore our team's expertise and the high-impact software solutions we've delivered across global markets." />
        <meta property="og:title" content="Case Studies | Winsil Evolution Pvt Ltd" />
        <meta property="og:description" content="Proven software success in Fintech, Healthcare, and Enterprise ERP Systems." />
      </Helmet>
      <PortfolioNavbar />

      <PageHero
        label="Case Studies"
        labelClass="text-orange-400"
        title="Our Team & Technical Expertise"
        subtitle="Meet the engineers driving software innovation and explore the technical depth of our high-impact software solutions."
        image="/images/photo-1551434678-e076c223a692.jpg"
        imageAlt="Engineering team working together"
        bgClass="bg-gray-950"
        glowColor="#f97316"
      />

      {/* ── Image Gallery ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-center mb-2 text-orange-500">Our Work in Action</p>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-10">Engineering That Makes an Impact</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { src: "/images/photo-1522071820081-009f0129c71c.jpg", alt: "Engineering team collaborating", label: "Our Team" },
              { src: "/images/photo-1516321318423-f06f85e504b3.jpg", alt: "Client project delivery", label: "Client Projects" },
              { src: "/images/photo-1551836022-d5d88e9218df.jpg", alt: "Analytics and data dashboard", label: "Fintech Solutions" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-64">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {[
              { src: "/images/photo-1559526324-4b87b5e36e44.jpg", alt: "Healthcare ERP platform", label: "Healthcare ERP" },
              { src: "/images/photo-1558494949-ef010cbdcc31.jpg", alt: "Cloud-native deployment", label: "Cloud Systems" },
            ].map((img, i) => (
              <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg group h-56">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <span className="text-white font-black text-sm uppercase tracking-wider">{img.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div className="bg-gray-900 border-y border-white/10 py-12 px-4">
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { value: "100+", label: "Projects Delivered" },
            { value: "50+",  label: "Engineers" },
            { value: "4",    label: "Industries Served" },
            { value: "99.9%", label: "Uptime SLA" },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-3xl sm:text-4xl font-black text-orange-400 mb-1">{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-widest text-white/40">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto pt-12 pb-12 px-4 sm:px-6 lg:px-8 z-10">

        <div className="bg-white p-8 rounded-xl shadow-xl mb-16 border-t-4 border-orange-500">
          <div className="flex flex-col lg:flex-row items-start lg:space-x-12">
            <div className="w-full lg:w-1/2 mt-8 lg:mt-0 p-6 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-500 h-full">
              <div className="flex items-center space-x-4 mb-6">
                <HexagonIcon color="linear-gradient(45deg, #e34040, #ff9900)">
                  <Users className="w-6 h-6 text-white" />
                </HexagonIcon>
                <h2 className="text-3xl font-bold text-orange-600">
                  A Dedicated Engineering Team
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {mindsetPoints.map((item, index) => (
                  <div
                    key={index}
                    className="flex flex-col space-y-1 p-3 bg-white rounded-lg shadow-sm border border-gray-200"
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <div className="flex-shrink-0 p-1 rounded-sm">
                        {item.icon}
                      </div>
                      <h4 className="text-lg font-extrabold text-gray-900">
                        {item.title}
                      </h4>
                    </div>
                    <p
                      className="text-gray-700 text-xs"
                      dangerouslySetInnerHTML={{ __html: item.detail }}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full lg:w-1/2 mt-8 lg:mt-0 p-6 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-600 h-full">
              <div className="flex items-center space-x-4 mb-6">
                <HexagonIcon color="linear-gradient(45deg, #ff8c00, #ff4500)">
                  <Lightbulb className="w-6 h-6 text-white" />
                </HexagonIcon>
                <h3 className="text-3xl font-bold text-orange-600">
                  Engineering Philosophy
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-6">
                {teamPhilosophy.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 p-2 h-10 w-10 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-xl font-extrabold text-gray-900 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-700 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-orange-600 text-white p-8 rounded-xl shadow-xl mb-16 border-t-4 border-orange-500">
          <h2 className="text-4xl font-bold text-orange-400 mb-8 text-center">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {coreValues.map((value, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-4 bg-gray-900 rounded-lg shadow-md"
              >
                <div className="p-3 bg-orange-500 rounded-full mb-3 shadow-lg">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold mb-2 text-orange-200">
                  {value.title}
                </h3>
                <p className="text-sm text-gray-200">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-8 rounded-xl shadow-xl mb-16 border-t-4 border-orange-500">
          <div className="flex flex-col-reverse lg:flex-row items-start lg:space-x-12">
            <div className="w-full lg:w-1/2 mb-8 lg:mb-0 p-6 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-500 h-full">
              <div className="flex items-center space-x-4 mb-6">
                <HexagonIcon color="linear-gradient(-45deg, #8b0000, #ff6347)">
                  <Globe className="w-6 h-6 text-white" />
                </HexagonIcon>
                <h3 className="text-3xl font-bold text-orange-600">
                  Technical Depth & Impact
                </h3>
              </div>
              <div className="flex flex-col gap-6">
                {impactMetrics.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-4 p-3 border border-gray-200 rounded-lg bg-white shadow-sm"
                  >
                    <div className="flex-shrink-0 p-2 h-10 w-10 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-extrabold text-gray-900 mb-1 leading-tight">
                        {item.metric}
                      </h4>
                      <p className="text-gray-700 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="w-full lg:w-1/2 mt-8 lg:mt-0 p-6 bg-orange-50 rounded-lg shadow-md border-l-4 border-orange-600 h-full">
              <div className="flex items-center space-x-4 mb-6">
                <HexagonIcon color="linear-gradient(45deg, #CC0000, #8b0000)">
                  <Briefcase className="w-6 h-6 text-white" />
                </HexagonIcon>
                <h2 className="text-3xl font-bold text-orange-600">
                  Proven Software Success
                </h2>
              </div>
              <ul className="space-y-5 text-lg text-gray-700">
                {workSummary.map((item, index) => (
                  <li
                    key={index}
                    className="p-3 border-b border-gray-300 flex items-start bg-white rounded-lg shadow-sm"
                  >
                    <Target className="w-5 h-5 mr-3 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="flex flex-col">
                      <span className="font-bold text-gray-900 text-lg">
                        {item.label}:
                      </span>
                      <span
                        className="text-sm"
                        dangerouslySetInnerHTML={{ __html: item.detail }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 p-8 rounded-xl shadow-inner border-t-4 border-orange-500">
          <h2 className="text-4xl font-bold text-orange-600 mb-8 border-b border-orange-200 pb-4">
            Technical Focus Areas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-12 gap-y-6">
            {strategicFocus.map((focus, index) => (
              <div
                key={index}
                className="p-4 border-l-4 border-orange-500 bg-white shadow-md flex items-start space-x-3"
              >
                <div className="flex-shrink-0 p-1 rounded-sm">
                  {focus.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1 leading-tight">
                    {focus.name}
                  </h3>
                  <p className="text-sm text-gray-700">{focus.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Why Winsil ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 mb-3">Why Winsil</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-6">
              A Team That Takes <span className="text-orange-500">Ownership</span>
            </h2>
            <p className="text-gray-600 text-base leading-relaxed mb-8">
              Our engineers don't just execute tickets — they partner with you. We bring product thinking
              and technical depth to every engagement, treating your codebase with the same care we'd give
              our own.
            </p>
            <ul className="space-y-3 mb-10">
              {[
                "Cross-functional teams: frontend, backend, cloud & DevOps",
                "Regular architecture and code quality reviews",
                "Weekly progress demos with live staging environments",
                "Transparent communication — no surprises, ever",
                "Post-launch monitoring, bug fixes, and feature support",
              ].map((item) => (
                <li key={item} className="flex items-center space-x-3">
                  <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700 text-sm font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg shadow-orange-500/25"
            >
              Work With Our Team <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
            <img
              src="/images/photo-1542744094-24638eff58bb.jpg"
              alt="Engineering team planning session"
              className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-gray-900/60 via-transparent to-orange-500/10" />
            <div className="absolute bottom-5 right-5 bg-white/95 backdrop-blur-sm rounded-xl px-5 py-3 shadow-xl">
              <p className="text-2xl font-black text-orange-600">50+</p>
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Expert Engineers</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── CTA Banner ── */}
      <div className="relative bg-gray-950 py-20 px-4 overflow-hidden text-white text-center">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-amber-400" />
        <div className="relative max-w-3xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-orange-400 mb-4">Let's Build Together</p>
          <h2 className="text-4xl sm:text-5xl font-black mb-6">Ready to Start Your Project?</h2>
          <p className="text-white/55 text-lg mb-10 max-w-xl mx-auto">Tell us what you're building and our team will be in touch within 24 hours with a tailored proposal.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-black uppercase tracking-wider rounded-lg bg-orange-500 text-white hover:bg-orange-400 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Start a Project <ArrowRight className="w-4 h-4 ml-2" />
            </button>
            <button
              onClick={() => navigate("/software-solutions")}
              className="inline-flex items-center justify-center px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-lg border-2 border-white/20 text-white/70 hover:border-orange-400 hover:text-white transition-all duration-300"
            >
              Explore Core Solutions
            </button>
          </div>
        </div>
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default OurWork;
